import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SmsIcon from '@mui/icons-material/Sms';
import InstagramIcon from '@mui/icons-material/Instagram';
import { ThemeContext } from '../ThemeContext';
import api from '../api';

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  // Hover colors: Light mode: #06bae4, Dark mode: #ff9e42
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Convert hex to rgba for shadows
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 10px 40px rgba(0, 0, 0, 0.1), 0 0 0 2px ${hexToRgba(hoverColor, 0.3)}, 0 0 20px ${hexToRgba(hoverColor, 0.5)}, 0 0 40px ${hexToRgba(hoverColor, 0.3)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      {children}
    </motion.div>
  );
};

// Animation state tracking to prevent refresh
let hasAnimatedInSession = false;

const ReferralPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState('');
  const [emailList, setEmailList] = useState([]);
  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const { isDarkMode } = useContext(ThemeContext);
  const hasAnimatedRef = useRef(false);

  // Memoize gradient for title
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  // Mark animations as started after first render
  useEffect(() => {
    if (!hasAnimatedInSession && !hasAnimatedRef.current) {
      hasAnimatedRef.current = true;
      hasAnimatedInSession = true;
    }
  }, []);

  // Fetch user profile with referral code
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userStr = localStorage.getItem('sat_user');
        const userData = userStr ? JSON.parse(userStr) : null;
        
        if (userData && userData.user_id) {
          const response = await api.get(`/api/user/get_user_profile/`);
          const profileData = response.data;
          
          // Update user data with referral code from profile
          const updatedUser = { ...userData, ...profileData };
          console.log('Updated user data with referral code:', updatedUser);
          setUser(updatedUser);
          
          // Update localStorage with the complete user data
          localStorage.setItem('sat_user', JSON.stringify(updatedUser));
        } else {
          setUser(userData);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Fallback to localStorage data
        const userStr = localStorage.getItem('sat_user');
        const userData = userStr ? JSON.parse(userStr) : null;
        setUser(userData);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Set referral code when user data is loaded
  useEffect(() => {
    if (user?.referral_code) {
      setReferralCode(user.referral_code);
    }
  }, [user]);

  // Fetch referral data when user is loaded
  useEffect(() => {
    const fetchReferrals = async () => {
      if (user?.user_id) {
        setLoadingReferrals(true);
        try {
          const response = await api.get(`/api/refer/get_referred_users/`);
          setReferrals(response.data.referrals || []);
          setResultData(response.data);
        } catch (error) {
          console.error('Error fetching referrals:', error);
          setReferrals([]); // Set empty array on error
        } finally {
          setLoadingReferrals(false);
        }
      }
    };

    fetchReferrals();
  }, [user]);

  const canRefer = user?.is_premium === true && Boolean(referralCode);
  const referralUnavailableMessage = user?.is_premium === true
    ? 'Your referral link is still being generated. Please refresh the page or contact support.'
    : 'You need an active subscription to refer somebody.';
  const frontendBaseUrl = process.env.REACT_APP_FRONTEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  const referralLink = canRefer ? `${frontendBaseUrl}/signup?ref=${referralCode}` : '';
  const referralMessage = canRefer ? `🎮 Hey! Come join GameYourSAT with me!

Test your knowledge, compete with friends, and improve your SAT scores with our interactive game platform.

Sign up and get premium to unlock all features + 5000 bonus points for both of us!

Join here: ${referralLink}

#SATPrep #Gaming #Education` : '';

  const addEmail = async () => {
    if (!canRefer) {
      alert(referralUnavailableMessage);
      return;
    }

    const email = emailInput.trim();
    if (!email || !isValidEmail(email) || emailList.includes(email)) {
      return;
    }

    setCheckingEmail(true);
    try {
      // Check if email already exists in the application
      const response = await api.get(`/api/refer/check_email_exists/${email}/`);
      
      if (response.data.exists) {
        alert(`This email (${email}) is already registered in our application. Please use a different email address.`);
        return;
      }
      
      // Email doesn't exist, add it to the list
      setEmailList([...emailList, email]);
      setEmailInput('');
    } catch (error) {
      console.error('Error checking email existence:', error);
      // If API fails, still allow adding the email (graceful fallback)
      setEmailList([...emailList, email]);
      setEmailInput('');
    } finally {
      setCheckingEmail(false);
    }
  };

  const removeEmail = (emailToRemove) => {
    setEmailList(emailList.filter(email => email !== emailToRemove));
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  const handleSendEmail = async () => {
    if (!canRefer) {
      alert(referralUnavailableMessage);
      return;
    }

    if (emailList.length === 0) {
      alert('Please add at least one email address');
      return;
    }

    try {
      setEmailSent(true);
      
      // Call backend API to send referral emails
      const response = await api.post('/api/refer/send_referral_email/', {
        email: emailList
      });
      
      if (response.status === 201) {
        setEmailList([]);
        
        // Refresh referrals list from API
        if (user?.user_id) {
          const refreshResponse = await api.get(`/api/refer/get_referred_users/`);
          setReferrals(refreshResponse.data.referrals || []);
          setResultData(refreshResponse.data);
        }
        
        // Show success message
        alert(`Successfully sent ${emailList.length} referral email(s)!`);
      }
    } catch (error) {
      console.error('Error sending referral emails:', error);
      alert('Failed to send referral emails. Please try again.');
    } finally {
      setEmailSent(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'subscribed': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-400 dark:border-green-600';
      case 'signed_up': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-400 dark:border-blue-600';
      case 'pending': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-400 dark:border-yellow-600';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'subscribed': return '✅';
      case 'signed_up': return '👤';
      case 'pending': return '⏳';
      default: return '❌';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalPointsEarned = resultData ? resultData.total_points_earned : 0;
  const successfulReferrals = resultData ? resultData.successful_referrals : 0;
  const totalCount = resultData ? resultData.total_count : 0;
  const hasInviteEmails = emailList.length > 0;
  const canSendInvite = canRefer && !emailSent && hasInviteEmails;
  const sendInviteButtonText = emailSent
    ? 'Emails Sent!'
    : hasInviteEmails
      ? `Send Invite to ${emailList.length} Email${emailList.length !== 1 ? 's' : ''}`
      : 'Enter an email above to send.';

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center text-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-24 pl-0 md:pl-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="inline-block mb-4"
          >
            <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full border-b-4 ${isDarkMode ? 'border-orange-500' : 'border-cyan-500'}`}></div>
          </motion.div>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 font-zonaProRegular">Loading your referral page...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Main Content */}
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 w-full max-w-7xl mx-auto">
        {/* Animated Header */}
        <motion.div
          initial={hasAnimatedRef.current ? false : { opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8"
        >
          <h1
            key={`title-${isDarkMode}`}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Refer Friends, Earn Points!
          </h1>
          <motion.p
            className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 font-zonaProRegular max-w-2xl mx-auto px-4"
            initial={hasAnimatedRef.current ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Invite friends to join GameYourSAT. When they subscribe, you both get <span className="font-bold" style={{ color: isDarkMode ? '#ff9e42' : '#06bae4' }}>5000 bonus points</span>!
          </motion.p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8">
          <Card delay={0.1} className="text-center p-4 sm:p-6">
            <div className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2 font-zonaProUltra`} style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>
              {successfulReferrals}
            </div>
            <div className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 font-zonaProRegular">Successful Referrals</div>
          </Card>
          <Card delay={0.2} className="text-center p-4 sm:p-6">
            <div className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2 font-zonaProUltra`} style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>
              {totalPointsEarned}
            </div>
            <div className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 font-zonaProRegular">Points Earned</div>
          </Card>
          <Card delay={0.3} className="text-center p-4 sm:p-6">
            <div className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2 font-zonaProUltra`} style={{ color: isDarkMode ? '#ff7a00' : '#212D82' }}>
              {totalCount}
            </div>
            <div className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 font-zonaProRegular">Total Invites Sent</div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Invite Section */}
          <div className="space-y-4 sm:space-y-6">
            {/* Share Your Invite */}
            <Card delay={0.4}>
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold font-zonaProUltra" style={{ color: isDarkMode ? '#ff7a00' : '#000000' }}>📱 Share Your Invite</h2>
              </div>
              
              {/* Referral Link */}
              <div className="mb-4 sm:mb-6">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-zonaProRegular">
                  Referral Link
                </label>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <input
                    type="text"
                    value={canRefer ? referralLink : referralUnavailableMessage}
                    readOnly
                    className={`flex-1 px-3 sm:px-4 py-2 sm:py-3 border rounded-lg bg-white dark:bg-gray-700 text-xs sm:text-sm font-zonaProRegular text-gray-900 dark:text-gray-100 ${canRefer ? 'border-gray-300 dark:border-gray-600' : 'border-orange-300 dark:border-orange-700'}`}
                  />
                  <motion.button 
                    onClick={() => handleCopy(referralLink)}
                    disabled={!canRefer}
                    whileHover={canRefer ? { scale: 1.05 } : {}}
                    whileTap={canRefer ? { scale: 0.95 } : {}}
                    className={`px-4 sm:px-6 py-2 sm:py-3 text-white rounded-lg transition-colors font-zonaProRegular flex items-center justify-center space-x-2 text-sm sm:text-base ${canRefer ? '' : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'}`}
                    style={{ backgroundColor: canRefer ? '#06bae4' : undefined }}
                  >
                    <ContentCopyIcon style={{ fontSize: '16px' }} />
                    <span>{copied ? 'Copied!' : 'Copy'}</span>
                  </motion.button>
                </div>
                {!canRefer && (
                  <div className="mt-3 rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-3">
                    <p className="text-sm text-orange-800 dark:text-orange-200 font-zonaProRegular">
                      {referralUnavailableMessage}
                    </p>
                    {user?.is_premium !== true && (
                      <a
                        href="/subscription"
                        className="inline-block mt-2 text-sm font-semibold underline font-zonaProRegular"
                        style={{ color: isDarkMode ? '#ff9e42' : '#06bae4' }}
                      >
                        View subscription options
                      </a>
                    )}
                  </div>
                )}
              </div>

              {/* Sharing Options */}
              <div className="flex items-center justify-center flex-wrap gap-2 sm:gap-3 md:gap-4">
                {/* Email */}
                <motion.a
                  href={canRefer ? `mailto:?subject=Join me on GameYourSAT!&body=${encodeURIComponent(referralMessage)}` : undefined}
                  whileHover={canRefer ? { scale: 1.1 } : {}}
                  whileTap={canRefer ? { scale: 0.9 } : {}}
                  className={`flex items-center justify-center p-2 sm:p-3 text-white rounded-full transition-colors ${canRefer ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed pointer-events-none'}`}
                  title="Email"
                  aria-label="Share by email"
                >
                  <EmailIcon style={{ fontSize: '18px', width: '18px', height: '18px' }} className="sm:w-5 sm:h-5" />
                </motion.a>

                {/* WhatsApp */}
                <motion.a
                  href={canRefer ? `https://wa.me/?text=${encodeURIComponent(referralMessage)}` : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={canRefer ? { scale: 1.1 } : {}}
                  whileTap={canRefer ? { scale: 0.9 } : {}}
                  className={`flex items-center justify-center p-2 sm:p-3 text-white rounded-full transition-colors ${canRefer ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed pointer-events-none'}`}
                  title="WhatsApp"
                  aria-label="Share on WhatsApp"
                >
                  <WhatsAppIcon style={{ fontSize: '18px', width: '18px', height: '18px' }} className="sm:w-5 sm:h-5" />
                </motion.a>

                {/* Messages / iMessage */}
                <motion.a
                  href={canRefer ? `sms:&body=${encodeURIComponent(referralMessage)}` : undefined}
                  whileHover={canRefer ? { scale: 1.1 } : {}}
                  whileTap={canRefer ? { scale: 0.9 } : {}}
                  className={`flex items-center justify-center p-2 sm:p-3 text-white rounded-full transition-colors ${canRefer ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed pointer-events-none'}`}
                  title="Messages / iMessage"
                  aria-label="Share through Messages or iMessage"
                >
                  <SmsIcon style={{ fontSize: '18px', width: '18px', height: '18px' }} className="sm:w-5 sm:h-5" />
                </motion.a>

                {/* Instagram DM */}
                <motion.a
                  href={canRefer ? "https://www.instagram.com/direct/inbox/" : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={canRefer ? () => handleCopy(referralMessage) : undefined}
                  whileHover={canRefer ? { scale: 1.1 } : {}}
                  whileTap={canRefer ? { scale: 0.9 } : {}}
                  className={`flex items-center justify-center p-2 sm:p-3 text-white rounded-full transition-colors ${canRefer ? 'bg-pink-500 hover:bg-pink-600' : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed pointer-events-none'}`}
                  title="Instagram DM"
                  aria-label="Copy invite text and open Instagram DM"
                >
                  <InstagramIcon style={{ fontSize: '18px', width: '18px', height: '18px' }} className="sm:w-5 sm:h-5" />
                </motion.a>

                {/* Copy Text */}
                <motion.button 
                  onClick={() => handleCopy(referralMessage)}
                  disabled={!canRefer}
                  whileHover={canRefer ? { scale: 1.1 } : {}}
                  whileTap={canRefer ? { scale: 0.9 } : {}}
                  className={`flex items-center justify-center p-2 sm:p-3 text-white rounded-full transition-colors ${canRefer ? 'bg-gray-500 hover:bg-gray-600' : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'}`}
                  title="Copy Text"
                  aria-label="Copy invite text"
                >
                  <ContentCopyIcon style={{ fontSize: '18px', width: '18px', height: '18px' }} className="sm:w-5 sm:h-5" />
                </motion.button>
              </div>
            </Card>

            {/* Direct Email Invite */}
            <Card delay={0.5}>
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold font-zonaProUltra" style={{ color: isDarkMode ? '#ff7a00' : '#000000' }}>📨 Send Direct Invite</h2>
              </div>
              {!canRefer ? (
                <div className="rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-4 text-center">
                  <p className="text-sm sm:text-base text-orange-800 dark:text-orange-200 font-zonaProRegular">
                    {referralUnavailableMessage}
                  </p>
                </div>
              ) : (
              <div className="space-y-3 sm:space-y-4">
                {/* Email Tags */}
                {emailList.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2 sm:mb-3">
                    <AnimatePresence>
                      {emailList.map((email, index) => (
                        <motion.div
                          key={email}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 text-white rounded-full text-xs sm:text-sm font-zonaProRegular"
                          style={{ backgroundColor: '#06bae4' }}
                        >
                          <span className="truncate max-w-[120px] sm:max-w-none">{email}</span>
                          <button
                            onClick={() => removeEmail(email)}
                            className="ml-1 hover:opacity-80 rounded-full p-0.5 transition-opacity flex-shrink-0"
                          >
                            <CloseIcon style={{ fontSize: '12px', width: '12px', height: '12px' }} className="sm:w-3.5 sm:h-3.5" />
                          </button>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}
                
                {/* Email Input */}
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Enter friend's email address"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={!canRefer}
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent font-zonaProRegular bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm sm:text-base"
                  />
                  <motion.button
                    onClick={addEmail}
                    disabled={!canRefer || !emailInput.trim() || !isValidEmail(emailInput.trim()) || emailList.includes(emailInput.trim()) || checkingEmail}
                    whileHover={canRefer && !checkingEmail && emailInput.trim() && isValidEmail(emailInput.trim()) && !emailList.includes(emailInput.trim()) ? { scale: 1.05 } : {}}
                    whileTap={canRefer && !checkingEmail && emailInput.trim() && isValidEmail(emailInput.trim()) && !emailList.includes(emailInput.trim()) ? { scale: 0.95 } : {}}
                    className={`px-3 sm:px-4 py-2 sm:py-3 text-white rounded-lg transition-colors font-zonaProRegular flex items-center justify-center ${
                      !canRefer || !emailInput.trim() || !isValidEmail(emailInput.trim()) || emailList.includes(emailInput.trim()) || checkingEmail
                        ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                        : 'hover:opacity-90'
                    }`}
                    style={{ 
                      backgroundColor: (!canRefer || !emailInput.trim() || !isValidEmail(emailInput.trim()) || emailList.includes(emailInput.trim()) || checkingEmail) ? undefined : '#06bae4'
                    }}
                    title={checkingEmail ? "Checking email..." : "Add email"}
                  >
                    {checkingEmail ? (
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                    ) : (
                      <AddIcon style={{ fontSize: '18px', width: '18px', height: '18px' }} className="sm:w-5 sm:h-5" />
                    )}
                  </motion.button>
                </div>
                
                {/* Send Button */}
                <motion.button
                  onClick={handleSendEmail}
                  disabled={!canSendInvite}
                  whileHover={canSendInvite ? { scale: 1.02 } : {}}
                  whileTap={canSendInvite ? { scale: 0.98 } : {}}
                  className="w-full py-2 sm:py-3 px-4 sm:px-6 text-white font-semibold rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-zonaProRegular flex items-center justify-center space-x-2 text-sm sm:text-base"
                  style={{ backgroundColor: canSendInvite ? '#06bae4' : undefined }}
                >
                  <EmailIcon style={{ fontSize: '16px', width: '16px', height: '16px' }} className="sm:w-4 sm:h-4" />
                  <span>{sendInviteButtonText}</span>
                </motion.button>
              </div>
              )}
            </Card>
          </div>

          {/* Referrals List */}
          <Card delay={0.6}>
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold font-zonaProUltra" style={{ color: isDarkMode ? '#ff7a00' : '#000000' }}>👥 Your Referrals</h2>
            </div>
            
            {loadingReferrals ? (
              <div className="text-center py-6 sm:py-8">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="inline-block mb-3 sm:mb-4"
                >
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full border-b-4 ${isDarkMode ? 'border-orange-500' : 'border-cyan-500'}`}></div>
                </motion.div>
                <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 font-zonaProRegular">Loading referrals...</p>
              </div>
            ) : referrals.length === 0 ? (
              <div className="text-center py-6 sm:py-8">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="inline-block mb-3 sm:mb-4"
                >
                  <div className="text-4xl sm:text-6xl">📭</div>
                </motion.div>
                <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 font-zonaProRegular">No referrals yet. Start inviting friends!</p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                <AnimatePresence>
                  {referrals.map((referral, index) => (
                    <motion.div
                      key={referral.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.1, duration: 0.4 }}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold font-zonaProUltra text-sm sm:text-base flex-shrink-0"
                               style={{ background: `linear-gradient(135deg, ${isDarkMode ? '#ff7a00' : '#212D82'}, ${isDarkMode ? '#ff9e42' : '#06bae4'})` }}>
                            {referral.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-gray-900 dark:text-gray-100 font-zonaProRegular text-sm sm:text-base truncate">{referral.name}</div>
                            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular truncate">{referral.email}</div>
                          </div>
                        </div>
                        <div className="flex flex-col sm:items-end gap-1.5 sm:gap-2">
                          <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-medium font-zonaProRegular border ${getStatusColor(referral.status)}`}>
                            {getStatusIcon(referral.status)} {referral.status}
                          </span>
                          {referral.pointsEarned > 0 && (
                            <div className="text-xs sm:text-sm font-medium font-zonaProRegular" style={{ color: '#06bae4' }}>
                              +{referral.pointsEarned} points
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 sm:mt-3 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-zonaProRegular space-y-1">
                        <div>Invited: {formatDate(referral.invitedAt)}</div>
                        {referral.signedUpAt && (
                          <div className="sm:inline sm:ml-4">
                            Signed Up: {formatDate(referral.signedUpAt)}
                          </div>
                        )}
                        {referral.subscribedAt && (
                          <div className="sm:inline sm:ml-4">
                            Subscribed: {formatDate(referral.subscribedAt)}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </Card>
        </div>

        {/* How It Works */}
        <Card delay={0.7} className="mt-6 sm:mt-8 md:mt-12">
          <div className="text-center mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 font-zonaProUltra" style={{ color: isDarkMode ? '#ff7a00' : '#000000' }}>🎯 How Referrals Work</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <motion.div 
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="inline-block mb-2 sm:mb-3"
              >
                <div className="text-3xl sm:text-4xl">📤</div>
              </motion.div>
              <h3 className="font-semibold mb-1 sm:mb-2 font-zonaProUltra text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#000000' }}>1. Share Your Link</h3>
              <p className="text-xs sm:text-sm opacity-90 font-zonaProRegular text-gray-600 dark:text-gray-400 px-2">Send your referral link to friends through Messages, Instagram DM, email, or WhatsApp</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
            >
              <motion.div 
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                className="inline-block mb-2 sm:mb-3"
              >
                <div className="text-3xl sm:text-4xl">🎮</div>
              </motion.div>
              <h3 className="font-semibold mb-1 sm:mb-2 font-zonaProUltra text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#000000' }}>2. Friend Joins & Subscribes</h3>
              <p className="text-xs sm:text-sm opacity-90 font-zonaProRegular text-gray-600 dark:text-gray-400 px-2">Your friend signs up and gets a subscription</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
            >
              <motion.div 
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
                className="inline-block mb-2 sm:mb-3"
              >
                <div className="text-3xl sm:text-4xl">🎉</div>
              </motion.div>
              <h3 className="font-semibold mb-1 sm:mb-2 font-zonaProUltra text-sm sm:text-base" style={{ color: isDarkMode ? '#ff7a00' : '#000000' }}>3. Both Get Rewards</h3>
              <p className="text-xs sm:text-sm opacity-90 font-zonaProRegular text-gray-600 dark:text-gray-400 px-2">You and your friend each receive 5000 bonus points!</p>
            </motion.div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ReferralPage;
